/**
 * Client-side task scanner for zero-config mode
 * Scans visible content in the browser without requiring API setup
 */

export interface ClientTask {
  id: string;
  type: string;
  content: string;
  file: string;
  line: number;
  priority: 'low' | 'medium' | 'high';
  source: 'dom' | 'props' | 'code' | 'sourcemap' | 'public';
}

export interface ScanOptions {
  includeComments?: boolean;
  includeHtml?: boolean;
  includeProps?: boolean;
  includeSourceMaps?: boolean;
  includePublicFiles?: boolean;
  includeAsync?: boolean;
  publicFiles?: string[];
  maxTasks?: number;
}

// Task patterns for different contexts
const TASK_PATTERNS = {
  // JavaScript/TypeScript comments
  jsComments: [
    /\/\/\s*(TODO|FIXME|HACK|NOTE|BUG|OPTIMIZE|REFACTOR):\s*(.+)/gi,
    /\/\*\s*(TODO|FIXME|HACK|NOTE|BUG|OPTIMIZE|REFACTOR):\s*(.+?)\s*\*\//gi,
  ],
  // HTML comments
  htmlComments: [
    /<!--\s*(TODO|FIXME|HACK|NOTE|BUG|OPTIMIZE|REFACTOR):\s*(.+?)\s*-->/gi,
  ],
  // Markdown
  markdown: [
    /[-*]\s*(TODO|FIXME|HACK|NOTE|BUG|OPTIMIZE|REFACTOR):\s*(.+)/gi,
    />\s*(TODO|FIXME|HACK|NOTE|BUG|OPTIMIZE|REFACTOR):\s*(.+)/gi,
  ],
};

/**
 * Scan DOM content for tasks
 */
export function scanDomContent(options: ScanOptions = {}): ClientTask[] {
  const tasks: ClientTask[] = [];
  const { includeComments = true, includeHtml = true, maxTasks = 100 } = options;

  if (!includeComments && !includeHtml) {
    return tasks;
  }

  // Get all text content from pre/code elements (likely contains code)
  if (includeComments) {
    const codeElements = document.querySelectorAll('pre, code');
    codeElements.forEach((element) => {
      const content = element.textContent || '';
      const elementTasks = scanTextContent(content, 'dom', 'DOM Element');
      tasks.push(...elementTasks);
    });
  }

  // Scan HTML comments in the page
  if (includeHtml) {
    const htmlContent = document.documentElement.innerHTML;
    TASK_PATTERNS.htmlComments.forEach((pattern) => {
      let match;
      while ((match = pattern.exec(htmlContent)) && tasks.length < maxTasks) {
        tasks.push({
          id: `client-html-${tasks.length}`,
          type: match[1].toLowerCase(),
          content: match[2].trim(),
          file: 'HTML Content',
          line: 0,
          priority: inferPriority(match[1], match[2]),
          source: 'dom',
        });
      }
    });
  }

  return tasks.slice(0, maxTasks);
}

/**
 * Scan Next.js props for tasks
 */
export function scanNextJsProps(options: ScanOptions = {}): ClientTask[] {
  const tasks: ClientTask[] = [];
  const { maxTasks = 100 } = options;

  // Check for Next.js __NEXT_DATA__
  if (typeof window !== 'undefined' && (window as any).__NEXT_DATA__) {
    const nextData = (window as any).__NEXT_DATA__;
    const propsString = JSON.stringify(nextData.props);
    
    // Scan stringified props for task patterns
    const propTasks = scanTextContent(propsString, 'props', 'Next.js Props');
    tasks.push(...propTasks.slice(0, maxTasks));
  }

  return tasks;
}

/**
 * Scan any text content for tasks
 */
export function scanTextContent(
  content: string,
  source: ClientTask['source'],
  fileName: string = 'Unknown'
): ClientTask[] {
  const tasks: ClientTask[] = [];
  const allPatterns = [
    ...TASK_PATTERNS.jsComments,
    ...TASK_PATTERNS.markdown,
  ];

  allPatterns.forEach((pattern) => {
    let match;
    const regex = new RegExp(pattern);
    while ((match = regex.exec(content))) {
      tasks.push({
        id: `client-${source}-${tasks.length}`,
        type: match[1].toLowerCase(),
        content: match[2].trim(),
        file: fileName,
        line: 0, // Can't determine line number in client-side scanning
        priority: inferPriority(match[1], match[2]),
        source,
      });
    }
  });

  return tasks;
}

/**
 * Infer task priority from type and content
 */
function inferPriority(type: string, content: string): ClientTask['priority'] {
  const upperType = type.toUpperCase();
  const lowerContent = content.toLowerCase();

  // High priority indicators
  if (
    upperType === 'FIXME' ||
    upperType === 'BUG' ||
    lowerContent.includes('urgent') ||
    lowerContent.includes('critical') ||
    lowerContent.includes('asap') ||
    lowerContent.includes('important')
  ) {
    return 'high';
  }

  // Low priority indicators
  if (
    upperType === 'NOTE' ||
    upperType === 'OPTIMIZE' ||
    lowerContent.includes('maybe') ||
    lowerContent.includes('consider') ||
    lowerContent.includes('later') ||
    lowerContent.includes('someday')
  ) {
    return 'low';
  }

  // Default to medium
  return 'medium';
}

/**
 * Main scanner that combines all scanning methods
 */
export function scanForTasks(options: ScanOptions = {}): ClientTask[] {
  const allTasks: ClientTask[] = [];
  const { maxTasks = 100 } = options;

  // Scan DOM content
  const domTasks = scanDomContent(options);
  allTasks.push(...domTasks);

  // Scan Next.js props if available
  const propTasks = scanNextJsProps(options);
  allTasks.push(...propTasks);

  // Remove duplicates based on type and content
  const uniqueTasks = allTasks.reduce((acc, task) => {
    const key = `${task.type}-${task.content}`;
    if (!acc.has(key)) {
      acc.set(key, task);
    }
    return acc;
  }, new Map<string, ClientTask>());

  return Array.from(uniqueTasks.values()).slice(0, maxTasks);
}

/**
 * Scan source maps for tasks
 */
export async function scanSourceMaps(options: ScanOptions = {}): Promise<ClientTask[]> {
  const tasks: ClientTask[] = [];
  const { maxTasks = 100 } = options;
  
  // Only scan in development
  if (process.env.NODE_ENV !== 'development') {
    return tasks;
  }

  try {
    const scripts = Array.from(document.scripts);
    
    for (const script of scripts) {
      if (script.src && script.src.endsWith('.js')) {
        try {
          const mapResponse = await fetch(script.src + '.map');
          if (mapResponse.ok) {
            const mapData = await mapResponse.json();
            
            // Extract sources and their content
            if (mapData.sourcesContent) {
              mapData.sources?.forEach((source: string, index: number) => {
                const content = mapData.sourcesContent[index];
                if (content) {
                  const sourceTasks = scanTextContent(
                    content,
                    'sourcemap',
                    source.split('/').pop() || 'Unknown'
                  );
                  tasks.push(...sourceTasks);
                }
              });
            }
          }
        } catch {
          // Ignore errors for individual source maps
        }
      }
      
      if (tasks.length >= maxTasks) break;
    }
  } catch (error) {
    console.debug('Error scanning source maps:', error);
  }

  return tasks.slice(0, maxTasks);
}

/**
 * Scan public files for tasks
 */
export async function scanPublicFiles(options: ScanOptions = {}): Promise<ClientTask[]> {
  const tasks: ClientTask[] = [];
  const { maxTasks = 100, publicFiles = [] } = options;
  
  // Default public files to check
  const defaultFiles = [
    '/README.md',
    '/CHANGELOG.md',
    '/TODO.md',
    '/docs/index.md',
    '/.tickets/index.md',
  ];
  
  const filesToScan = [...new Set([...defaultFiles, ...publicFiles])];
  
  for (const file of filesToScan) {
    try {
      const response = await fetch(file);
      if (response.ok) {
        const content = await response.text();
        const fileTasks = scanTextContent(
          content,
          'public',
          file.split('/').pop() || 'Unknown'
        );
        tasks.push(...fileTasks);
      }
    } catch {
      // File doesn't exist or not accessible, skip
    }
    
    if (tasks.length >= maxTasks) break;
  }
  
  return tasks.slice(0, maxTasks);
}

/**
 * Enhanced async scanner that includes all sources
 */
export async function scanForTasksAsync(options: ScanOptions = {}): Promise<ClientTask[]> {
  const allTasks: ClientTask[] = [];
  const { 
    maxTasks = 100,
    includeSourceMaps = false,
    includePublicFiles = false, // Changed to false to avoid 404s in fresh projects
  } = options;

  // Get synchronous tasks first
  const syncTasks = scanForTasks(options);
  allTasks.push(...syncTasks);

  // Add async sources
  if (includeSourceMaps) {
    const sourceMapTasks = await scanSourceMaps(options);
    allTasks.push(...sourceMapTasks);
  }

  if (includePublicFiles) {
    const publicTasks = await scanPublicFiles(options);
    allTasks.push(...publicTasks);
  }

  // Remove duplicates
  const uniqueTasks = allTasks.reduce((acc, task) => {
    const key = `${task.type}-${task.content}`;
    if (!acc.has(key)) {
      acc.set(key, task);
    }
    return acc;
  }, new Map<string, ClientTask>());

  return Array.from(uniqueTasks.values()).slice(0, maxTasks);
}

/**
 * Watch for DOM changes and rescan
 */
export function watchForChanges(
  callback: (tasks: ClientTask[]) => void,
  options: ScanOptions = {}
): () => void {
  let timeoutId: NodeJS.Timeout;

  const debouncedScan = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(async () => {
      // Use async scanner if async sources are enabled
      const tasks = options.includeSourceMaps || options.includePublicFiles
        ? await scanForTasksAsync(options)
        : scanForTasks(options);
      callback(tasks);
    }, 500);
  };

  // Initial scan
  debouncedScan();

  // Watch for DOM changes
  const observer = new MutationObserver(debouncedScan);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Cleanup function
  return () => {
    observer.disconnect();
    clearTimeout(timeoutId);
  };
}